<template>
  <div class="distribution-circle">
    <!-- État de calibrage -->
    <div v-if="isCalibrating" class="calibration-state">
      <div class="calibration-circle">
        <div class="calibration-spinner"></div>
        <div class="calibration-content">
          <div class="calibration-title">Calibrage</div>
          <div class="calibration-timer">{{ formatTime(remainingTime) }}</div>
          <div class="calibration-subtitle">en cours...</div>
        </div>
      </div>
      <div class="calibration-text">
        Collecte des données en cours
      </div>
    </div>

    <!-- Composant principal -->
    <div v-else class="main-content">
      <!-- Cercle donut -->
      <div class="donut-container" :class="{ 'waiting-state': !exerciseStarted }">
        <svg class="donut-chart" :width="size" :height="size" viewBox="0 0 120 120">
          <!-- Segment T1 (Vert) -->
          <circle
            v-if="exerciseStarted"
            class="donut-segment"
            cx="60"
            cy="60"
            :r="radius"
            fill="transparent"
            stroke="#4CAF50"
            :stroke-width="strokeWidth"
            :stroke-dasharray="t1Arc + ' ' + circumference"
            :stroke-dashoffset="0"
            transform="rotate(-90 60 60)"
          />
          
          <!-- Segment T2 (Orange) -->
          <circle
            v-if="exerciseStarted"
            class="donut-segment"
            cx="60"
            cy="60"
            :r="radius"
            fill="transparent"
            stroke="#FF9800"
            :stroke-width="strokeWidth"
            :stroke-dasharray="t2Arc + ' ' + circumference"
            :stroke-dashoffset="t2Offset"
            transform="rotate(-90 60 60)"
          />
          
          <!-- Segment T3 (Rouge) -->
          <circle
            v-if="exerciseStarted"
            class="donut-segment"
            cx="60"
            cy="60"
            :r="radius"
            fill="transparent"
            stroke="#F44336"
            :stroke-width="strokeWidth"
            :stroke-dasharray="t3Arc + ' ' + circumference"
            :stroke-dashoffset="t3Offset"
            transform="rotate(-90 60 60)"
          />
        </svg>
        
        <!-- Centre avec cohérence cardiaque ou état d'attente -->
        <div class="center-content" :class="{ 'waiting-center': !exerciseStarted }">
          <div v-if="!exerciseStarted" class="waiting-content">
            <!-- <div class="waiting-dot"></div> -->
            <div class="waiting-text">En attente</div>
          </div>
          <div v-else class="cc-value">{{ Math.round(coherenceValue) }}%</div>
        </div>
      </div>
      
      <!-- Légende compacte -->
      <div class="legend-compact" :class="{ 'waiting-legend': !exerciseStarted }">
        <div class="legend-row">
          <div class="legend-item" :class="{ 'waiting-item': !exerciseStarted }">
            <div class="legend-dot t1-color" :class="{ 'waiting-dot': !exerciseStarted }"></div>
            <span class="legend-text">T1</span>
            <span class="legend-value">{{ exerciseStarted ? pctT1 : '—' }}%</span>
          </div>
          <div class="legend-item" :class="{ 'waiting-item': !exerciseStarted }">
            <div class="legend-dot t2-color" :class="{ 'waiting-dot': !exerciseStarted }"></div>
            <span class="legend-text">T2</span>
            <span class="legend-value">{{ exerciseStarted ? pctT2 : '—' }}%</span>
          </div>
          <div class="legend-item" :class="{ 'waiting-item': !exerciseStarted }">
            <div class="legend-dot t3-color" :class="{ 'waiting-dot': !exerciseStarted }"></div>
            <span class="legend-text">T3</span>
            <span class="legend-value">{{ exerciseStarted ? pctT3 : '—' }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DistributionPicsCircle",
  props: {
    pctT1: {
      type: Number,
      default: 0
    },
    pctT2: {
      type: Number,
      default: 0
    },
    pctT3: {
      type: Number,
      default: 0
    },
    coherenceValue: {
      type: Number,
      default: 0
    },
    exerciseStarted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      size: 150, // Taille identique au BpmBubble (150px)
      radius: 40, // Rayon ajusté pour le donut
      strokeWidth: 15, // Épaisseur du donut ajustée
      isCalibrating: false,
      startTime: null,
      remainingTime: 80,
      timer: null
    };
  },
  watch: {
    exerciseStarted(newVal) {
      if (newVal && !this.isCalibrating) {
        this.startCalibration();
      }
    }
  },
  computed: {
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    
    // Calcul des arcs pour chaque segment
    t1Arc() {
      return (this.pctT1 / 100) * this.circumference;
    },
    
    t2Arc() {
      return (this.pctT2 / 100) * this.circumference;
    },
    
    t3Arc() {
      return (this.pctT3 / 100) * this.circumference;
    },
    
    // Calcul des offsets pour positionner les segments
    t2Offset() {
      return -this.t1Arc;
    },
    
    t3Offset() {
      return -(this.t1Arc + this.t2Arc);
    }
  },
  mounted() {
    // Le calibrage ne démarre plus automatiquement
    // Il sera déclenché par le watcher quand exerciseStarted devient true
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    startCalibration() {
      this.isCalibrating = true;
      this.startTime = Date.now();
      this.remainingTime = 80;
      this.startCalibrationTimer();
    },
    
    startCalibrationTimer() {
      this.timer = setInterval(() => {
        const currentTime = Date.now();
        const elapsedTime = (currentTime - this.startTime) / 1000;
        this.remainingTime = Math.max(0, 80 - Math.floor(elapsedTime));
        
        if (elapsedTime >= 80) {
          this.isCalibrating = false;
          clearInterval(this.timer);
        }
      }, 1000);
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
.distribution-circle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 10px;
  box-sizing: border-box;
}

/* États de calibrage - Taille cohérente avec BpmBubble */
.calibration-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.calibration-circle {
  position: relative;
  width: 150px; /* Taille identique au BpmBubble */
  height: 150px;
  margin-bottom: 15px;
  border: 10px solid #fff; /* Bordure comme BpmBubble */
  border-radius: 50%;
  background: #f8f9fa;
  box-shadow: 0 0 10px #00000094; /* Ombre identique au BpmBubble */
}

.calibration-spinner {
  position: absolute;
  top: -10px; /* Compensation pour la bordure */
  left: -10px;
  width: 150px;
  height: 150px;
  border: 10px solid #f3f3f3;
  border-top: 10px solid #489181;
  border-radius: 50%;
  animation: spin 2s linear infinite;
}

.calibration-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 100px;
}

.calibration-title {
  font-size: 14px;
  font-weight: 600;
  color: #489181;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.calibration-timer {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  line-height: 1;
  margin-bottom: 4px;
}

.calibration-subtitle {
  font-size: 12px;
  color: #777;
  font-style: italic;
}

.calibration-text {
  font-size: 14px;
  color: #666;
  text-align: center;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Contenu principal */
.main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  animation: fadeInUp 0.8s ease-out;
}

.donut-container {
  position: relative;
  display: flex;
  /* align-items: center; */
  justify-content: center;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
  transition: all 0.5s ease;
  /* Bordure et ombre comme BpmBubble */
  /* border: 10px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 10px #00000094;
  background: #fff; */
}

.donut-container.waiting-state {
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
  border-color: #e9ecef;
}

.donut-chart {
  transform: rotate(0deg);
}

@keyframes waitingPulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}

.donut-segment {
  transition: all 0.8s ease-in-out;
  opacity: 0.95;
}

.donut-segment:hover {
  opacity: 1;
  filter: brightness(1.1);
}

.center-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  background: #ffffff;
  border-radius: 50%;
  width: 90px; /* Ajusté pour la nouvelle taille */
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #f8f9fa;
  transition: all 0.5s ease;
}

.center-content.waiting-center {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-color: #dee2e6;
}

.waiting-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.waiting-dot {
  width: 8px;
  height: 8px;
  background: #6c757d;
  border-radius: 50%;
  animation: gentlePulse 2s ease-in-out infinite;
}

@keyframes gentlePulse {
  0%, 100% { opacity: 0.7; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
}

.waiting-text {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cc-value {
  font-size: 28px;
  font-weight: bold;
  color: #2c3e50;
  line-height: 1;
}

.legend-compact {
  width: 100%;
  max-width: 180px;
  transition: all 0.5s ease;
}

.legend-compact.waiting-legend {
  opacity: 0.7;
}

.legend-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.legend-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex: 1;
  padding: 8px 6px;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.legend-item.waiting-item {
  background: rgba(248, 249, 250, 0.5);
}

.legend-item:hover {
  background: rgba(248, 249, 250, 1);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legend-item.waiting-item:hover {
  background: rgba(248, 249, 250, 0.7);
  transform: none;
  box-shadow: none;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-bottom: 2px;
  transition: all 0.3s ease;
}

.legend-dot.waiting-dot {
  opacity: 0.4;
  transform: scale(0.8);
}

.legend-text {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  line-height: 1;
}

.legend-value {
  font-size: 13px;
  font-weight: bold;
  color: #333;
  line-height: 1;
}

.waiting-item .legend-value {
  color: #6c757d;
  font-style: italic;
}

.t1-color {
  background-color: #4CAF50;
}

.t2-color {
  background-color: #FF9800;
}

.t3-color {
  background-color: #F44336;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Animation progressive des segments */
.donut-segment:nth-child(2) {
  animation: drawSegment 1.2s ease-out 0.2s both;
}

.donut-segment:nth-child(3) {
  animation: drawSegment 1.2s ease-out 0.5s both;
}

.donut-segment:nth-child(4) {
  animation: drawSegment 1.2s ease-out 0.8s both;
}

@keyframes drawSegment {
  from {
    stroke-dasharray: 0 1000;
  }
  to {
    stroke-dasharray: 1000 1000;
  }
}

/* Responsive - Cohérent avec BpmBubble */
@media (max-width: 768px) {
  .calibration-circle {
    width: 120px;
    height: 120px;
    border-width: 8px;
  }
  
  .calibration-spinner {
    top: -8px;
    left: -8px;
    width: 120px;
    height: 120px;
    border-width: 8px;
  }
  
  .calibration-timer {
    font-size: 20px;
  }
  
  .donut-container {
    border-width: 8px;
  }
  
  .center-content {
    width: 75px;
    height: 75px;
  }
  
  .cc-value {
    font-size: 24px;
  }
  
  .waiting-text {
    font-size: 10px;
  }
  
  .legend-item {
    padding: 6px 4px;
  }
  
  .legend-text,
  .legend-value {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .calibration-circle {
    width: 100px;
    height: 100px;
    border-width: 6px;
  }
  
  .calibration-spinner {
    top: -6px;
    left: -6px;
    width: 100px;
    height: 100px;
    border-width: 6px;
  }
  
  .donut-container {
    border-width: 6px;
  }
  
  .center-content {
    width: 65px;
    height: 65px;
  }
  
  .cc-value {
    font-size: 20px;
  }
  
  .legend-row {
    gap: 6px;
  }
  
  .legend-item {
    padding: 4px 2px;
  }
  
  .legend-text,
  .legend-value {
    font-size: 10px;
  }
}
</style>